import React from 'react'
import NetworkErrorModal from '../../components/NetworkErrorModal'
import Logo from '@bt-react/components/Logo'
import getEnvironment from '@bt-react/libs/getEnvironment'
import { debug } from '@bt-react/libs/Logger'
import get from 'lodash/get'
import styles from './Access.module.scss'
import { AccessProps, AccessState } from './types'
import { ServiceError } from '@bt-react/constants'
import { isObserver, isPresenter, isDialIn } from '../../lib/username/username'
import AccessModalHorizontal from '../../components/AccessModalHorizontal'

class Access extends React.Component<AccessProps, AccessState> {

  networkErrorCheck
  _MAX_USER_SERVICE_RETRIES = 5
  _MOUNTED

  constructor (props) {

    super(props)
    this.state = {
      isUserServiceRetrying: false,
      channelDataRequestSent: false,
      hasError: false,
      hasNetworkError: false,
      userServiceRetry: 0,
      webcastDataRequestSent: false,
      userSettingsRequestSent: false
    }

  }

  componentDidMount () {

    debug('Opened Access page')
    this._MOUNTED = true
    this.setRoomname()
    this.props.fetchUserData()
    if (typeof this.props.setAvailableDevices !== 'undefined') this.props.setAvailableDevices()

  }

  componentDidUpdate (prevProps) {

    if (!this.props.isFetchingUserData && prevProps.isFetchingUserData) this.handleUserServiceResponse()

    if (
      this.state.hasNetworkError &&
      !this.state.isUserServiceRetrying &&
      this.state.userServiceRetry < this._MAX_USER_SERVICE_RETRIES
    ) {

      this.setState({ isUserServiceRetrying: true, userServiceRetry: this.state.userServiceRetry + 1 }, () => {

        if (this._MOUNTED) this.networkErrorCheck = window.setTimeout(this.props.fetchUserData, 5000, true)

      })

    }

    /** Clear the retry logic if hasValidUser is true */
    if (prevProps.hasValidUser !== this.props.hasValidUser && this.props.hasValidUser) {

      debug(`Valid user`)
      clearInterval(this.networkErrorCheck)

    }

    if (!this.props.host && typeof this.props.setHost !== 'undefined') this.props.setHost()

    this.fetchRequiredData()

    this.checkUrl()

  }

  componentWillUnmount () {

    this._MOUNTED = false

  }

  fetchRequiredData () {

    const { channelId, webcastId } = this.props

    if (!this.props.hasValidUser) return
    if (!channelId || !webcastId) return


    if (!this.state.userSettingsRequestSent) {

      debug(`Fetching user settings`)
      this.props.fetchUserSettings()
      this.setState({ userSettingsRequestSent: true })

    }

    if (this.props.hasChannelAccess === null) {

      if (!this.state.channelDataRequestSent) {

        debug(`Fetching channel data`)
        this.props.fetchChannelData(channelId)
        this.setState({ channelDataRequestSent: true })

      }

      return

    }

    if (this.state.channelDataRequestSent) {

      debug(`${this.props.hasChannelAccess ? 
        'Has' 
        : 'Does not have'} channel access is ${this.props.hasChannelAccess} for ${channelId}-${webcastId}`
      )
      this.setState({ channelDataRequestSent: false })

    }

    if (!this.props.hasChannelAccess) return

    if (!this.props.date && !this.state.webcastDataRequestSent && !this.props.hasWebcastData) {

      debug(`Fetching webcast data`)
      this.props.fetchPublicWebcastData(channelId, webcastId)
      this.setState({ webcastDataRequestSent: true })

    }

    if (this.state.webcastDataRequestSent && this.props.hasWebcastData) {

      debug(`Has webcast data for ${channelId}-${webcastId}`)
      this.setState({ webcastDataRequestSent: false })

    }

  }

  /**
   * Check whether a network error has occurred once the user service has responded
   */
  handleUserServiceResponse = () => {

    this.setState({
      hasNetworkError: this.props.errorResponse.message === ServiceError.NETWORK_ERROR,
      isUserServiceRetrying: false
    })

  }

  setHost = () => {

    const host = getEnvironment()
    return typeof host.publicServices !== 'undefined' &&
      typeof this.props.setHost !== 'undefined'
      ? this.props.setHost(host.publicServices)
      : null

  }

  setRoomname = () => {

    const roomname = this.props.roomname ? this.props.roomname : get(this.props, 'match.params.roomname').split('&')[0]

    if (!roomname) return this.setState({ hasError: true })
    return this.props.setRoomname(roomname)

  }

  get isEnvironmentSetup () {

    return this.props.hasValidUser &&
      this.props.hasChannelAccess &&
      this.props.hasWebcastData &&
      this.props.hasDocs &&
      !this.props.hasGigEnded &&
      this.props.hasUserSettings

  }

  checkUrl = (): void => {

    /** If the user is logged in without a password, redirect to the login page and after login redirect to presenting pin page */
    if (this.props.userIsPasswordlessLogin === 'true') return window.location.replace(`${window.location.origin}/login?redirect=/presenting`)

    /** If we don't have a valid user and it's not due to a network error, redirect to the login page */
    if (this.props.hasValidUser === false && !this.state.hasNetworkError) {

      if (getEnvironment().domain === 'localhost') return

      return window.location.replace(
        `${window.location.origin}/login?redirect=${window.location.pathname}${window.location.search}`
      )
    }

    /** If the user doesn't have access to the channel, redirect to the PIN access page */
    if (this.props.hasChannelAccess === false) return window.location.replace(`${window.location.origin}/presenting`)

    /** If an error code has been set, forward to the messageCenter */
    if (this.props.messageCode) return this.props.history.replace(`/messageCenter/${this.props.messageCode}`)

    if (!this.props.hasChannelAccess || !this.props.hasWebcastData || !this.props.hasValidUser) return

    if (this.props.hasGigEnded) return this.props.history.replace(`/end/${this.props.roomname}`)

    if (this.isEnvironmentSetup) {

      if (isObserver(this.props.username || '')) return this.checkObserverUrl()
      if (isPresenter(this.props.username || '')) return this.checkPresenterUrl()
      if (isDialIn(this.props.username || '')) return this.checkModeratorUrl()

    }

  }

  checkObserverUrl = (): void => {

    const { roomname } = this.props
    const search = get(this.props, 'location.search', '')
    return this.props.history.replace(`/present/${roomname}${search}`)

  }

  checkPresenterUrl = (): void => {

    const { roomname } = this.props
    const search = get(this.props, 'location.search', '')
    return this.props.history.replace(`/preview/${roomname}${search}`)

  }

  checkModeratorUrl = (): void => {

    const { roomname } = this.props
    const search = get(this.props, 'location.search', '')
    return this.props.history.replace(`/moderator/${roomname}${search}`)

  }

  render () {

    if (this.state.hasNetworkError) return <NetworkErrorModal />

    if (
      this.props.hasValidUser &&
      this.props.hasChannelAccess &&
      this.props.date
    ) return <AccessModalHorizontal date={this.props.date} />
    else {

      return <div className={styles['Logo--fullscreen']}>
        <Logo animated={true} width={300} />
      </div>

    }

  }

}

export default Access
